import React, { useState, useEffect } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'
import * as XLSX from 'xlsx'

const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', blueD:'#2563eb', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

function fmt(v) {
    if (v === null || v === undefined || isNaN(v)) return '—'
    const n = Number(v)
    if (n === 0) return '—'
    return n.toLocaleString('es-MX', { minimumFractionDigits:2, maximumFractionDigits:2 })
}

export default function ReporteSociedadesRevisadas() {
    const history = useHistory()
    const { id }  = useParams()

    const defaultEjercicio = new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear()
    const defaultPeriodo   = MESES[new Date().getMonth() === 0 ? 11 : new Date().getMonth() - 1]

    const [ejercicio, setEjercicio] = useState(defaultEjercicio)
    const [periodo,   setPeriodo]   = useState(defaultPeriodo)
    const [reportes,  setReportes]  = useState([])
    const [loading,      setLoading]   = useState(false)
    const [hasSearched,  setHasSearched] = useState(false)

    // paginación
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 50

    const fetchData = (ejr, per) => {
        setLoading(true)
        ElementsDataService.retrieveAllElements(`cfdis/reporte-clientes-sociedades-revisadas/${ejr}/${per}`)
            .then(r => { setReportes(r.data || []); setPage(1); setHasSearched(true) })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('READ_CONTABILIDAD')) {
            history.push('/login'); return
        }
    }, []) // eslint-disable-line

    /* ── Excel ── */
    const downloadExcel = () => {
        const data = reportes.map((r, i) => ({
            'N°':                                    i + 1,
            'Cliente':                               r.clienteName,
            'Total Sociedades':                      r.totalSociedades,
            'Activas':                               r.sociedadesActivas,
            'Sin Utilizar':                          r.sociedadesSinUtilizar,
            'Inactivas':                             r.sociedadesInactivas,
            'Proc. Liquidación':                     r.sociedadesProcesoLiquidacion,
            'Liquidadas':                            r.sociedadesLiquidadas,
            'Proc. Suspensión':                      r.sociedadesProcesoSuspension,
            'Suspendidas':                           r.sociedadesSuspendidas,
            'Vendidas':                              r.sociedadesVendidas,
            'Devueltas':                             r.sociedadesDevueltas,
            'No Revisión':                           r.sociedadesNoRevision,
            'Sin Estatus':                           r.sociedadesSinEstatus,
            'Dom. Control':                          r.totalDomControl,
            'Dom. No Control':                       r.totalDomNoControl,
            'Dom. Pend. Cambio':                     r.totalDomPendienteDeCambio,
            'Dom. Sin Estatus':                      r.totalDomSinStatus,
            'Total Domicilios':                      r.totalDomicilios,
            [`PPM al 23/${periodo}/${ejercicio}`]:   r.sociedadesPPMantes,
            [`PPM después 23/${periodo}/${ejercicio}`]: r.sociedadesPPMdespues,
            'Sin Declarar':                          r.sociedadesSinDeclarar,
            [`Ingresos Dec. SAT ${periodo} ${ejercicio}`]: r.ingresosDeclaradosPeriodoEjercicio,
            [`Ingresos Fact. SAT ${periodo} ${ejercicio}`]: r.ingresosTimbradosPeriodoEjercicio,
            [`Nómina Timbrada ${periodo} ${ejercicio}`]:   r.nominaTimbradaPeriodoEjercicio,
        }))
        const ws = XLSX.utils.json_to_sheet(data)
        ws['!cols'] = [
            {wch:4},{wch:20},{wch:10},{wch:9},{wch:12},{wch:10},{wch:14},{wch:12},
            {wch:14},{wch:12},{wch:10},{wch:10},{wch:12},{wch:11},
            {wch:12},{wch:14},{wch:16},{wch:14},{wch:14},
            {wch:16},{wch:18},{wch:12},{wch:20},{wch:20},{wch:20},
        ]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, `${periodo} ${ejercicio}`)
        XLSX.writeFile(wb, `Reporte_Sociedades_Revisadas_${periodo}_${ejercicio}.xlsx`)
    }

    /* ── estilos ── */
    const thS = { padding:'9px 12px', fontSize:10, fontWeight:700, color:T.txt2,
        textTransform:'uppercase', letterSpacing:'.04em', whiteSpace:'nowrap',
        borderBottom:`2px solid ${T.border}`, background:'#f8fafc',
        borderRight:`1px solid ${T.border}`, textAlign:'center' }
    const thG = (bg) => ({ padding:'7px 12px', fontSize:10, fontWeight:800, color:'white',
        background:bg, textAlign:'center', borderRight:`1px solid rgba(255,255,255,.25)`,
        letterSpacing:'.04em', whiteSpace:'nowrap' })
    const tdC = { padding:'9px 12px', fontSize:12, color:T.txt, borderBottom:`1px solid ${T.border}`,
        borderRight:`1px solid ${T.border}`, textAlign:'center', verticalAlign:'middle' }
    const tdN = { ...tdC, fontVariantNumeric:'tabular-nums', fontFamily:'monospace', fontSize:11 }
    const tdM = { ...tdN, color:T.blueD, fontWeight:600 }

    const totalPages = Math.ceil(reportes.length / PAGE_SIZE)
    const paginated  = reportes.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE)

    if (loading) return (
        <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:T.font }}>
            <div style={{ textAlign:'center' }}>
                <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTopColor:T.blue,
                    borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 12px' }} />
                <p style={{ color:T.txt2, fontSize:14, margin:0 }}>Cargando {periodo} {ejercicio}...</p>
            </div>
            <style>{`@keyframes spin { to { transform:rotate(360deg) } }`}</style>
        </div>
    )

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`}</style>

            <div style={{ maxWidth:'100%' }}>

                {/* ── Header ── */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <button onClick={() => history.goBack()}
                            style={{ width:34, height:34, borderRadius:9, border:`1px solid ${T.border}`,
                                background:T.card, cursor:'pointer', fontSize:16, color:T.txt2,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                boxShadow:T.shadow, flexShrink:0, fontFamily:T.font, transition:'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                            onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                            ‹
                        </button>
                        <div>
                            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>
                                Reporte de Sociedades Revisadas
                            </h1>
                            <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>
                                {periodo} {ejercicio} · {reportes.length} clientes
                            </p>
                        </div>
                    </div>
                    <button onClick={downloadExcel} disabled={reportes.length === 0}
                        style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 18px',
                            borderRadius:T.rsm, border:'none', background: reportes.length === 0 ? T.muted : T.blue,
                            color:'white', fontSize:13, fontWeight:700, cursor: reportes.length === 0 ? 'not-allowed':'pointer',
                            fontFamily:T.font, boxShadow: reportes.length > 0 ? `0 4px 12px ${T.blue}40`:'none' }}>
                        📥 Descargar Excel
                    </button>
                </div>

                {/* ── Controles periodo/ejercicio ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r,
                    boxShadow:T.shadow, padding:'16px 20px', marginBottom:16,
                    display:'flex', gap:16, alignItems:'flex-end', flexWrap:'wrap' }}>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <label style={{ fontSize:11, fontWeight:600, color:T.txt2, textTransform:'uppercase', letterSpacing:'.05em' }}>
                            Ejercicio
                        </label>
                        <input type="number" min="1900" max="2099" step="1" value={ejercicio}
                            onChange={e => { setEjercicio(Number(e.target.value)) }}
                            style={{ width:100, padding:'8px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                fontSize:13, color:T.txt, fontFamily:T.font, outline:'none', background:T.card }} />
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <label style={{ fontSize:11, fontWeight:600, color:T.txt2, textTransform:'uppercase', letterSpacing:'.05em' }}>
                            Periodo
                        </label>
                        <select value={periodo}
                            onChange={e => { setPeriodo(e.target.value) }}
                            style={{ padding:'8px 12px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                fontSize:13, color:T.txt, fontFamily:T.font, background:T.card, cursor:'pointer', outline:'none' }}>
                            {MESES.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <button onClick={() => fetchData(ejercicio, periodo)} disabled={loading}
                        style={{ padding:'9px 20px', borderRadius:T.rsm, border:'none',
                            background: loading ? T.muted : T.blue, color:'white',
                            fontSize:13, fontWeight:700, cursor: loading ? 'not-allowed':'pointer',
                            fontFamily:T.font, display:'inline-flex', alignItems:'center', gap:8,
                            boxShadow: loading ? 'none' : `0 4px 12px ${T.blue}40`, transition:'all .15s',
                            alignSelf:'flex-end' }}>
                        {loading ? (
                            <>
                                <div style={{ width:14, height:14, border:'2px solid #ffffff40', borderTopColor:'white',
                                    borderRadius:'50%', animation:'spin .7s linear infinite' }} />
                                Buscando...
                            </>
                        ) : '🔍 Buscar'}
                    </button>
                </div>

                {/* ── Tabla ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`,
                        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span>📋</span>
                            <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>Clientes</h2>
                            <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`,
                                border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>{reportes.length}</span>
                        </div>
                        {reportes.length > PAGE_SIZE && (
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                                    style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`,
                                        background:page===1?T.bg:T.card, color:page===1?T.muted:T.txt,
                                        cursor:page===1?'default':'pointer', fontSize:13, fontFamily:T.font }}>‹</button>
                                <span style={{ fontSize:12, color:T.txt2, whiteSpace:'nowrap' }}>
                                    {page} / {totalPages}
                                    <span style={{ color:T.muted }}> ({(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,reportes.length)} de {reportes.length})</span>
                                </span>
                                <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
                                    style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`,
                                        background:page===totalPages?T.bg:T.card, color:page===totalPages?T.muted:T.txt,
                                        cursor:page===totalPages?'default':'pointer', fontSize:13, fontFamily:T.font }}>›</button>
                            </div>
                        )}
                    </div>

                    {!hasSearched ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ fontSize:40, marginBottom:10 }}>📅</div>
                            <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.txt }}>Selecciona ejercicio y periodo</p>
                            <p style={{ margin:'6px 0 0', fontSize:13, color:T.muted }}>Presiona Buscar para cargar el reporte</p>
                        </div>
                    ) : reportes.length === 0 ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ fontSize:36, marginBottom:10 }}>📊</div>
                            <p style={{ margin:0, fontSize:14, color:T.muted }}>Sin datos para {periodo} {ejercicio}</p>
                        </div>
                    ) : (
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ borderCollapse:'collapse', minWidth:2000, width:'100%' }}>
                                <thead>
                                    {/* Grupos */}
                                    <tr>
                                        <th colSpan={2}  style={thG('#475569')}>GENERAL</th>
                                        <th colSpan={12} style={thG('#3b82f6')}>ESTATUS SOCIEDADES</th>
                                        <th colSpan={5}  style={thG('#0891b2')}>DOMICILIOS</th>
                                        <th colSpan={3}  style={thG('#7c3aed')}>PPM / DECLARACIONES</th>
                                        <th colSpan={3}  style={thG('#059669')}>{`FINANCIERO ${periodo} ${ejercicio}`}</th>
                                    </tr>
                                    <tr>
                                        {['N°','Cliente',
                                          'Total','Activas','Sin Utilizar','Inactivas','Proc. Liq.','Liquidadas','Proc. Sus.','Suspendidas','Vendidas','Devueltas','No Revisión','Sin Estatus',
                                          'Control','No Control','Pend. Cambio','Sin Estatus','Total Dom.',
                                          `PPM ≤23/${periodo}`,`PPM >23/${periodo}`,'Sin Declarar',
                                          'Ing. Dec. SAT','Ing. Fact. SAT','Nómina Timbrada'
                                        ].map(h => <th key={h} style={thS}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((r, i) => (
                                        <tr key={r.clienteId ?? i}
                                            onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(c => c.style.background=`${T.blue}06`) }}
                                            onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(c => c.style.background= i%2===0?T.card:T.bg) }}>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg, color:T.muted, fontWeight:700 }}>{(page-1)*PAGE_SIZE+i+1}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg, textAlign:'left', fontWeight:500, whiteSpace:'nowrap' }}>
                                                <Link to={`/clientes/reportes/${r.clienteId}`}
                                                    style={{ color:T.blue, textDecoration:'none' }}>{r.clienteName}</Link>
                                            </td>
                                            {/* Estatus */}
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg, fontWeight:700 }}>{r.totalSociedades ?? '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg, color:T.green }}>{r.sociedadesActivas || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesSinUtilizar || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesInactivas || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesProcesoLiquidacion || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesLiquidadas || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesProcesoSuspension || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesSuspendidas || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesVendidas || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesDevueltas || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesNoRevision || '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesSinEstatus || '—'}</td>
                                            {/* Domicilios */}
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.totalDomControl ?? '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.totalDomNoControl ?? '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.totalDomPendienteDeCambio ?? '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.totalDomSinStatus ?? '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg, fontWeight:700 }}>{r.totalDomicilios ?? '—'}</td>
                                            {/* PPM */}
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesPPMantes ?? '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesPPMdespues ?? '—'}</td>
                                            <td style={{ ...tdC, background:i%2===0?T.card:T.bg }}>{r.sociedadesSinDeclarar ?? '—'}</td>
                                            {/* Financiero */}
                                            <td style={{ ...tdM, background:i%2===0?T.card:T.bg }}>{fmt(r.ingresosDeclaradosPeriodoEjercicio)}</td>
                                            <td style={{ ...tdM, background:i%2===0?T.card:T.bg }}>{fmt(r.ingresosTimbradosPeriodoEjercicio)}</td>
                                            <td style={{ ...tdM, background:i%2===0?T.card:T.bg }}>{fmt(r.nominaTimbradaPeriodoEjercicio)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}
